import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import { DirectionService } from '../../direction.service';
import { BranchesService } from '../../super-management/branch/branches.service';

export interface ResendPartialFilesDialogData {
  order: any;
  orderType: 'plotter' | 'express';
  forceRasterize?: boolean;
  expressPrintCode?: number;
  expressServerAddress?: string;
}

interface FileSelectionState {
  fileChecked: boolean;
  pagesChecked: boolean[];
}

@Component({
  selector: 'app-resend-partial-files-dialog',
  templateUrl: './resend-partial-files-dialog.component.html',
  styleUrls: ['./resend-partial-files-dialog.component.css'],
  host: { class: 'fill-screen-modal-order-summary' },
})
export class ResendPartialFilesDialogComponent implements OnInit {
  isRTL = true;
  isDarkMode = false;
  fileSelections: FileSelectionState[] = [];
  isSubmitting = false;
  forceRasterize = false;

  constructor(
    private directionService: DirectionService,
    private branchesService: BranchesService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private dialogRef: MatDialogRef<ResendPartialFilesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ResendPartialFilesDialogData
  ) {
    this.directionService.direction$.subscribe((d) => (this.isRTL = d === 'rtl'));
    this.directionService.isDarkMode$.subscribe((d) => (this.isDarkMode = d));
    this.forceRasterize = data?.forceRasterize === true;
  }

  ngOnInit(): void {
    const files = this.files;
    this.fileSelections = files.map((file) => ({
      fileChecked: true,
      pagesChecked: (file?.images || []).map(() => true),
    }));
  }

  get order(): any {
    return this.data?.order || {};
  }

  get orderType(): 'plotter' | 'express' {
    return this.data?.orderType || 'plotter';
  }

  get files(): any[] {
    return this.order?.files || [];
  }

  get isPlotter(): boolean {
    return this.orderType === 'plotter';
  }

  get isExpress(): boolean {
    return this.orderType === 'express';
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  /** true when at least one file/page is marked for resend */
  get canResend(): boolean {
    return this.fileSelections.some((selection) =>
      selection.pagesChecked?.some((pageChecked) => pageChecked)
    );
  }

  buildSelectionsPayload(): {
    fileId: string;
    fileIncluded: boolean;
    pagesIncluded: number[];
  }[] {
    return this.files.map((file, fileIndex) => {
      const selection = this.fileSelections[fileIndex];
      const pagesIncluded = (selection?.pagesChecked || [])
        .map((checked, pageIndex) => (checked ? pageIndex : -1))
        .filter((pageIndex) => pageIndex >= 0);
      return {
        fileId: String(file._id),
        fileIncluded: selection?.fileChecked === true && pagesIncluded.length > 0,
        pagesIncluded,
      };
    });
  }

  onResend(): void {
    if (this.isSubmitting || !this.canResend || !this.order?._id) {
      return;
    }

    const selections = this.buildSelectionsPayload();
    const forceRasterizeSent = this.isExpress && this.forceRasterize;
    const expressPrintCode = this.data?.expressPrintCode;
    const expressServerAddress = this.data?.expressServerAddress;

    this.isSubmitting = true;
    this.branchesService
      .resendPartialOrder(
        this.order._id,
        this.orderType,
        selections,
        forceRasterizeSent
      )
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.dialogRef.close(true);

          const showSuccessSnack = () => {
            setTimeout(() => {
              this.snackBar.open(
                this.translate.instant('su-management.resend-order.success'),
                '',
                {
                  duration: 5000,
                  verticalPosition: 'top',
                }
              );
            }, 2000);
          };

          if (this.isExpress && response?.newOrderId) {
            if (expressPrintCode == null || expressPrintCode === 0) {
              this.snackBar.open(
                this.translate.instant('su-management.resend-partial-files.error.print-failed'),
                '',
                { duration: 8000, verticalPosition: 'top' }
              );
              return;
            }
            this.branchesService
              .triggerExpressOrderPrint(
                expressServerAddress || '',
                response.newOrderId,
                expressPrintCode,
                forceRasterizeSent
              )
              .subscribe({
                next: () => showSuccessSnack(),
                error: () => {
                  this.snackBar.open(
                    this.translate.instant('su-management.resend-partial-files.error.print-failed'),
                    '',
                    { duration: 8000, verticalPosition: 'top' }
                  );
                },
              });
            return;
          }

          showSuccessSnack();
        },
        error: (error) => {
          this.isSubmitting = false;
          const messageKey = this.getPartialResendErrorMessageKey(error);
          this.snackBar.open(this.translate.instant(messageKey), '', {
            duration: 8000,
            verticalPosition: 'top',
          });
        },
      });
  }

  private getPartialResendErrorMessageKey(error: any): string {
    const serverMessage = error?.error?.message;
    if (serverMessage === 'QUEUE_NOT_EMPTY') {
      return 'su-management.resend-partial-files.error.queue-not-empty';
    }
    if (serverMessage === 'NO_FILES_TO_RESEND') {
      return 'su-management.resend-partial-files.error.no-files';
    }
    if (serverMessage === 'ORDER_NOT_FOUND') {
      return 'su-management.resend-partial-files.error.order-not-found';
    }
    if (serverMessage === 'PRINTER_NOT_FOUND') {
      return 'su-management.resend-partial-files.error.printer-not-found';
    }
    if (
      serverMessage === 'PARTIAL_RESEND_PRINT_FAILED' ||
      serverMessage === 'PAYMENT_ERROR' ||
      serverMessage === 'NO_PRINTER_FOUND_WITH_THIS_CODE' ||
      serverMessage === 'ERROR_MOVING_FILES' ||
      serverMessage === 'PAYMENT_ERRORRROR_MOVING_FILES'
    ) {
      return 'su-management.resend-partial-files.error.print-failed';
    }
    return 'su-management.resend-partial-files.error.generic';
  }

  onFileCheckboxChange(fileIndex: number, checked: boolean): void {
    const selection = this.fileSelections[fileIndex];
    if (!selection) {
      return;
    }
    selection.fileChecked = checked;
    selection.pagesChecked = selection.pagesChecked.map(() => checked);
  }

  onPageCheckboxChange(fileIndex: number, pageIndex: number, checked: boolean): void {
    const selection = this.fileSelections[fileIndex];
    if (!selection) {
      return;
    }
    selection.pagesChecked[pageIndex] = checked;
    selection.fileChecked = selection.pagesChecked.some((pageChecked) => pageChecked);
  }

  isFileChecked(fileIndex: number): boolean {
    return this.fileSelections[fileIndex]?.fileChecked ?? false;
  }

  isPageChecked(fileIndex: number, pageIndex: number): boolean {
    return this.fileSelections[fileIndex]?.pagesChecked?.[pageIndex] ?? false;
  }

  getDisplayFileNameAfterSplit(file: any): string {
    if (!file) {
      return '';
    }
    if (file.originalFileName && String(file.originalFileName).trim() !== '') {
      return file.originalFileName;
    }
    const rawName = file.fileName || file.name || '';
    if (!rawName) {
      return '';
    }
    const parts = String(rawName).split('-');
    return parts.length > 1 ? parts[1] : rawName;
  }

  getPlotterImageSrc(fileIndex: number, pageIndex: number): string {
    const order = this.order;
    const image = order?.files?.[fileIndex]?.images?.[pageIndex];
    const thumbnail = image?.thumbnailPath;
    if (!thumbnail) {
      return '';
    }
    const cacheKey = [
      String(order?._id ?? ''),
      String(fileIndex),
      String(pageIndex),
      String(thumbnail),
      String(order?.printerID?.serverAddress ?? ''),
    ].join('\x1e');
    if (
      image.__cachedPlotterImgKey === cacheKey &&
      typeof image.__cachedPlotterImgUrl === 'string'
    ) {
      return image.__cachedPlotterImgUrl;
    }
    const isNewThumbnail =
      thumbnail.startsWith('uploads') ||
      thumbnail.startsWith('/home/ubuntu/IMG-Plotter');
    let newThumbnail = '';
    if (isNewThumbnail) {
      const serverAddress = order?.printerID?.serverAddress || '';
      if (thumbnail.startsWith('uploads')) {
        newThumbnail = serverAddress + '/' + thumbnail;
      } else {
        const shortThumbnail = thumbnail.replace('/home/ubuntu/IMG-Plotter', '');
        newThumbnail = serverAddress + shortThumbnail;
      }
    } else {
      newThumbnail = thumbnail;
    }
    image.__cachedPlotterImgKey = cacheKey;
    image.__cachedPlotterImgUrl = newThumbnail;
    return newThumbnail;
  }

  getExpressImageSrc(fileIndex: number, pageIndex: number): string {
    const image = this.files?.[fileIndex]?.images?.[pageIndex];
    return image?.path || image?.thumbnailPath || '';
  }

  getPrintSettings(source: any): any {
    return source?.printSettings || source?.settings || {};
  }

  getPaperSerialNamePlotter(paperCode: string, inputBins: any[]): string | null {
    if (!paperCode || !inputBins?.length) {
      return null;
    }
    const match = inputBins.find(
      (paper) => paper?.paperType?.paperPrinterCode === paperCode
    );
    return match?.paperType?.paperType ?? null;
  }

  getPaperSerialNameExpress(paperType: string, papers: any[]): string | null {
    if (!paperType || !papers?.length) {
      return null;
    }
    const match = papers.find((paper) => paper?.paperName === paperType);
    return match?.serial_name ?? null;
  }

  getPlotterPaperSerialName(image: any): string | null {
    const settings = this.getPrintSettings(image);
    return this.getPaperSerialNamePlotter(
      settings.paperType,
      this.order?.printerID?.inputBins
    );
  }

  getExpressPaperSerialName(file: any): string | null {
    const settings = this.getPrintSettings(file);
    return this.getPaperSerialNameExpress(
      settings.paperType,
      this.order?.printerID?.consumables?.papers
    );
  }

  getPlotterScale(image: any): number {
    if (image?.scale != null && !isNaN(Number(image.scale))) {
      return Number(image.scale);
    }
    if (image?.resizeFactor != null && !isNaN(Number(image.resizeFactor))) {
      return Number(image.resizeFactor) / 100;
    }
    if (image?.origImageHeight && image?.imageHeight) {
      return Number(image.imageHeight) / Number(image.origImageHeight);
    }
    return 1;
  }

  roundDownTowDecimalCeil(num: number): number {
    return Math.ceil(num * 100) / 100;
  }

  getPlotterDimension(image: any, dimension: 'height' | 'width'): number {
    const value =
      dimension === 'height'
        ? image?.imageHeight ?? image?.height
        : image?.imageWidth ?? image?.width;
    if (value == null || isNaN(Number(value))) {
      return 0;
    }
    return Math.round((Number(value) / 300) * 2.54 * 100) / 100;
  }

  roundDownTowDecimal(num: number): number {
    return Math.round(num * 100) / 100;
  }
}
